import React, { useState, useEffect } from 'react';
import { Bell, Mail, Send, TestTube, Save, Clock, Server, Lock, UserPlus, X, Plus, Database, Cloud, HardDrive, RefreshCw, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api, { getSettings, updateSettings, getSmtpConfig, updateSmtpConfig, sendTestEmail, sendNotifications, getUsers, getStorageConfig, updateStorageConfig, testStorageConnection, migrateStorageToS3, getAiConfig, updateAiConfig, testAiConnection } from '../services/api';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const [settings, setSettings] = useState({
    notification_enabled: true,
    notification_days: [30, 15, 7],
    notification_frequency: 'daily',
    recipients: []
  });
  const [daysInput, setDaysInput] = useState('30, 15, 7');

  const [smtp, setSmtp] = useState({
    smtp_server: '',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Licença360'
  });
  const [smtpPasswordSet, setSmtpPasswordSet] = useState(false);

  // Storage (Object Storage) config
  const [storage, setStorage] = useState({
    provider: 'emergent',
    s3_access_key_id: '',
    s3_secret_access_key: '',
    s3_region: '',
    s3_bucket: '',
    s3_endpoint: ''
  });
  const [s3SecretSet, setS3SecretSet] = useState(false);
  const [savingStorage, setSavingStorage] = useState(false);
  const [testingStorage, setTestingStorage] = useState(false);
  const [storageStatus, setStorageStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const [externalEmail, setExternalEmail] = useState('');
  const [externalName, setExternalName] = useState('');

  // AI (Gemini) config
  const [ai, setAi] = useState({ model: 'gemini-3.8-flash', api_key: '', enabled: true });
  const [aiKeySet, setAiKeySet] = useState(false);
  const [savingAi, setSavingAi] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, smtpRes, usersRes] = await Promise.all([
        getSettings(),
        getSmtpConfig(),
        getUsers().catch(() => ({ data: [] }))
      ]);
      setSettings(settingsRes.data);
      setDaysInput(settingsRes.data.notification_days?.join(', ') || '30, 15, 7');
      setSmtp({
        smtp_server: smtpRes.data.smtp_server || '',
        smtp_port: smtpRes.data.smtp_port || 465,
        smtp_user: smtpRes.data.smtp_user || '',
        smtp_password: '',
        from_email: smtpRes.data.from_email || '',
        from_name: smtpRes.data.from_name || 'Licença360'
      });
      setSmtpPasswordSet(smtpRes.data.smtp_password_set);
      setAllUsers(usersRes.data || []);
      try {
        const storageRes = await getStorageConfig();
        setStorage({
          provider: storageRes.data.provider || 'emergent',
          s3_access_key_id: storageRes.data.s3_access_key_id || '',
          s3_secret_access_key: '',
          s3_region: storageRes.data.s3_region || '',
          s3_bucket: storageRes.data.s3_bucket || '',
          s3_endpoint: storageRes.data.s3_endpoint || ''
        });
        setS3SecretSet(storageRes.data.s3_secret_set);
      } catch (e) { /* silent */ }
      try {
        const aiRes = await getAiConfig();
        setAi({ model: aiRes.data.model || 'gemini-3.8-flash', api_key: '', enabled: aiRes.data.enabled !== false });
        setAiKeySet(aiRes.data.api_key_set);
      } catch (e) { /* silent */ }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const days = daysInput.split(',').map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0);
      if (days.length === 0) {
        toast.error('Informe pelo menos um dia para notificação');
        setSaving(false);
        return;
      }
      await updateSettings({
        ...settings,
        notification_days: days
      });
      setSettings(prev => ({ ...prev, notification_days: days }));
      toast.success('Configurações de notificação salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSavingSmtp(true);
    try {
      const data = { ...smtp };
      if (!data.smtp_password) delete data.smtp_password;
      await updateSmtpConfig(data);
      setSmtpPasswordSet(true);
      toast.success('Configuração SMTP salva!');
    } catch (error) {
      toast.error('Erro ao salvar configuração SMTP');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSaveStorage = async () => {
    if (storage.provider === 's3') {
      if (!storage.s3_access_key_id || !storage.s3_bucket || (!storage.s3_secret_access_key && !s3SecretSet)) {
        toast.error('Para usar o AWS S3, informe Access Key, Bucket e Secret Access Key');
        return;
      }
    }
    setSavingStorage(true);
    try {
      const data = { ...storage };
      if (!data.s3_secret_access_key) delete data.s3_secret_access_key;
      const res = await updateStorageConfig(data);
      setS3SecretSet(res.data.s3_secret_set);
      setStorage(prev => ({ ...prev, s3_secret_access_key: '' }));
      toast.success('Configuração de armazenamento salva!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração de armazenamento');
    } finally {
      setSavingStorage(false);
    }
  };

  const handleTestStorage = async () => {
    setTestingStorage(true);
    setStorageStatus(null);
    try {
      const res = await testStorageConnection();
      setStorageStatus(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar comunicação com o storage');
    } finally {
      setTestingStorage(false);
    }
  };

  const handleMigrateToS3 = async () => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await migrateStorageToS3();
      setMigrateResult(res.data);
      const d = res.data;
      toast.success(`Migração concluída: ${d.migrated} enviado(s), ${d.already_present} já existente(s), ${d.not_found} não encontrado(s), ${d.failed} falha(s).`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao migrar anexos para o S3');
    } finally {
      setMigrating(false);
    }
  };

  const handleSaveAi = async () => {
    setSavingAi(true);
    try {
      const data = { model: ai.model, enabled: ai.enabled };
      if (ai.api_key) data.api_key = ai.api_key;
      const res = await updateAiConfig(data);
      setAiKeySet(res.data.api_key_set);
      setAi((prev) => ({ ...prev, api_key: '' }));
      toast.success('Configuração de IA salva!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração de IA');
    } finally {
      setSavingAi(false);
    }
  };

  const handleTestAi = async () => {
    setTestingAi(true);
    setAiTestResult(null);
    try {
      const res = await testAiConnection();
      setAiTestResult(res.data);
      (res.data.ok ? toast.success : toast.error)(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar conexão com o Gemini');
    } finally {
      setTestingAi(false);
    }
  };

  const handleTestEmail = async () => {
    if (settings.recipients.length === 0) {
      toast.error('Adicione pelo menos um destinatário');
      return;
    }
    setTesting(true);
    try {
      const res = await sendTestEmail();
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email de teste');
    } finally {
      setTesting(false);
    }
  };

  const handleSendNotifications = async () => {
    setSending(true);
    try {
      const res = await sendNotifications();
      toast.success(`${res.data.emails_sent} email(s) enviado(s) para ${res.data.licenses_found} licença(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar notificações');
    } finally {
      setSending(false);
    }
  };

  const addUserRecipient = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    if (!user) return;
    if (settings.recipients.some(r => r.email === user.email)) {
      toast.error('Este destinatário já foi adicionado');
      return;
    }
    const newRecipient = { type: 'user', email: user.email, name: user.name, user_id: user.id };
    setSettings(prev => ({ ...prev, recipients: [...prev.recipients, newRecipient] }));
  };

  const addExternalRecipient = () => {
    if (!externalEmail || !externalEmail.includes('@')) {
      toast.error('Informe um email válido');
      return;
    }
    if (settings.recipients.some(r => r.email === externalEmail)) {
      toast.error('Este email já foi adicionado');
      return;
    }
    const newRecipient = { type: 'external', email: externalEmail, name: externalName || externalEmail };
    setSettings(prev => ({ ...prev, recipients: [...prev.recipients, newRecipient] }));
    setExternalEmail('');
    setExternalName('');
  };

  const removeRecipient = (email) => {
    setSettings(prev => ({ ...prev, recipients: prev.recipients.filter(r => r.email !== email) }));
  };

  const availableUsers = allUsers.filter(u => !settings.recipients.some(r => r.user_id === u.id));

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Configurações</h1>
        <p className="text-gray-500 mt-1">Gerencie o servidor de email e as notificações de vencimento</p>
      </div>

      {/* SMTP Configuration */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Server className="w-5 h-5 text-[#003366]" />
            Configuração do Servidor de Email (SMTP)
          </CardTitle>
          <CardDescription>Configure o servidor de saída para envio de emails</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Servidor SMTP</Label>
              <Input
                value={smtp.smtp_server}
                onChange={(e) => setSmtp({ ...smtp, smtp_server: e.target.value })}
                placeholder="Ex: smtps.uhserver.com"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-server-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Porta</Label>
              <Input
                type="number"
                value={smtp.smtp_port}
                onChange={(e) => setSmtp({ ...smtp, smtp_port: parseInt(e.target.value) || 465 })}
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-port-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Usuário SMTP</Label>
              <Input
                value={smtp.smtp_user}
                onChange={(e) => setSmtp({ ...smtp, smtp_user: e.target.value })}
                placeholder="Ex: no-reply@empresa.com"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-user-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" />
                Senha SMTP
              </Label>
              <Input
                type="password"
                value={smtp.smtp_password}
                onChange={(e) => setSmtp({ ...smtp, smtp_password: e.target.value })}
                placeholder={smtpPasswordSet ? '••••••••' : 'Informe a senha'}
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Email de Envio (From)</Label>
              <Input
                type="email"
                value={smtp.from_email}
                onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
                placeholder="Ex: no-reply@empresa.com"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-from-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Nome do Remetente</Label>
              <Input
                value={smtp.from_name}
                onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })}
                placeholder="Ex: Licença360"
                className="bg-white border-gray-300 text-gray-900"
                data-testid="smtp-from-name-input"
              />
            </div>
          </div>
          <Button
            onClick={handleSaveSmtp}
            disabled={savingSmtp}
            className="bg-[#003366] hover:bg-[#002244]"
            data-testid="save-smtp-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {savingSmtp ? 'Salvando...' : 'Salvar Configuração SMTP'}
          </Button>
        </CardContent>
      </Card>

      {/* Storage Configuration */}
      <Card className="bg-white border-gray-200" data-testid="storage-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <HardDrive className="w-5 h-5 text-[#003366]" />
            Armazenamento de Anexos (Storage)
          </CardTitle>
          <CardDescription>
            Escolha onde os anexos de licenças e chamados são guardados e verifique a comunicação com cada provedor.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Communication Status */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-gray-700 font-medium">Status de Comunicação</Label>
              <Button
                onClick={handleTestStorage}
                disabled={testingStorage}
                variant="outline"
                size="sm"
                className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                data-testid="test-storage-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${testingStorage ? 'animate-spin' : ''}`} />
                {testingStorage ? 'Testando...' : 'Testar comunicação'}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Managed */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid="storage-status-managed">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Database className="w-4 h-4 text-[#003366]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">Storage Gerenciado (Emergent)</p>
                  {storageStatus ? (
                    <p className={`text-xs flex items-center gap-1 ${storageStatus.managed.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {storageStatus.managed.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      {storageStatus.managed.message}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">Clique em "Testar comunicação"</p>
                  )}
                </div>
                {storage.provider === 'emergent' && (
                  <Badge variant="outline" className="bg-blue-50 text-[#003366] border-blue-200 text-xs">Ativo</Badge>
                )}
              </div>
              {/* S3 */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid="storage-status-s3">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Cloud className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">AWS S3</p>
                  {storageStatus ? (
                    <p className={`text-xs flex items-center gap-1 ${storageStatus.s3.ok ? 'text-emerald-600' : (storageStatus.s3.configured ? 'text-rose-600' : 'text-gray-400')}`}>
                      {storageStatus.s3.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : (storageStatus.s3.configured ? <XCircle className="w-3.5 h-3.5" /> : null)}
                      {storageStatus.s3.message}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">Clique em "Testar comunicação"</p>
                  )}
                </div>
                {storage.provider === 's3' && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Ativo</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Provider selector */}
          <div className="space-y-2">
            <Label className="text-gray-700 font-medium">Provedor Ativo</Label>
            <Select
              value={storage.provider}
              onValueChange={(v) => setStorage({ ...storage, provider: v })}
            >
              <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="storage-provider-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="emergent">Storage Gerenciado (Emergent) — padrão</SelectItem>
                <SelectItem value="s3">AWS S3 (seu bucket)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">
              Ao selecionar AWS S3, os novos anexos passam a ser gravados no seu bucket. Arquivos antigos continuam acessíveis via storage gerenciado.
            </p>
          </div>

          {/* S3 fields */}
          {storage.provider === 's3' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-amber-50/40 rounded-lg border border-amber-100" data-testid="s3-config-fields">
              <div className="space-y-2">
                <Label className="text-gray-700">Access Key ID</Label>
                <Input
                  value={storage.s3_access_key_id}
                  onChange={(e) => setStorage({ ...storage, s3_access_key_id: e.target.value })}
                  placeholder="Ex: AKIA..."
                  className="bg-white border-gray-300 text-gray-900 font-mono"
                  data-testid="s3-access-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5" /> Secret Access Key
                </Label>
                <Input
                  type="password"
                  value={storage.s3_secret_access_key}
                  onChange={(e) => setStorage({ ...storage, s3_secret_access_key: e.target.value })}
                  placeholder={s3SecretSet ? '••••••••' : 'Informe a secret key'}
                  className="bg-white border-gray-300 text-gray-900 font-mono"
                  data-testid="s3-secret-key-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Região</Label>
                <Input
                  value={storage.s3_region}
                  onChange={(e) => setStorage({ ...storage, s3_region: e.target.value })}
                  placeholder="Ex: us-east-1"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="s3-region-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Bucket</Label>
                <Input
                  value={storage.s3_bucket}
                  onChange={(e) => setStorage({ ...storage, s3_bucket: e.target.value })}
                  placeholder="Ex: meu-bucket-anexos"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="s3-bucket-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-700">Endpoint (opcional)</Label>
                <Input
                  value={storage.s3_endpoint}
                  onChange={(e) => setStorage({ ...storage, s3_endpoint: e.target.value })}
                  placeholder="Deixe em branco para AWS. Ex: https://s3.compat.exemplo.com"
                  className="bg-white border-gray-300 text-gray-900"
                  data-testid="s3-endpoint-input"
                />
                <p className="text-xs text-gray-500">Use apenas para provedores compatíveis com S3 (MinIO, Wasabi, etc.).</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handleSaveStorage}
              disabled={savingStorage}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="save-storage-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {savingStorage ? 'Salvando...' : 'Salvar Armazenamento'}
            </Button>
            {s3SecretSet && (
              <Button
                onClick={() => {
                  if (window.confirm('Copiar todos os anexos existentes (licenças e chamados) para o bucket S3? Os arquivos já presentes no S3 serão ignorados. Nada é apagado do storage atual.')) {
                    handleMigrateToS3();
                  }
                }}
                disabled={migrating}
                variant="outline"
                className="bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="migrate-s3-btn"
              >
                <Cloud className={`w-4 h-4 mr-2 ${migrating ? 'animate-pulse' : ''}`} />
                {migrating ? 'Migrando anexos...' : 'Migrar anexos para o S3'}
              </Button>
            )}
          </div>

          {migrateResult && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 text-sm" data-testid="migrate-result">
              <p className="font-semibold text-gray-900 mb-1">Resultado da migração</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-gray-700">
                <span>Total: <b>{migrateResult.total}</b></span>
                <span className="text-emerald-600">Enviados: <b>{migrateResult.migrated}</b></span>
                <span className="text-blue-600">Já existentes: <b>{migrateResult.already_present}</b></span>
                <span className="text-amber-600">Não encontrados: <b>{migrateResult.not_found}</b></span>
                <span className="text-rose-600">Falhas: <b>{migrateResult.failed}</b></span>
              </div>
              {migrateResult.errors && migrateResult.errors.length > 0 && (
                <ul className="mt-2 list-disc list-inside text-xs text-rose-600">
                  {migrateResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI (Gemini) Configuration */}
      <Card className="bg-white border-gray-200" data-testid="ai-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Sparkles className="w-5 h-5 text-[#003366]" />
            Validação por IA (Google Gemini)
          </CardTitle>
          <CardDescription>
            Usada para ler os documentos anexados e sugerir correções nas licenças. A chave é guardada criptografada.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <Lock className="w-3.5 h-3.5" /> Chave de API do Google Gemini
              </Label>
              <Input
                type="password"
                value={ai.api_key}
                onChange={(e) => setAi({ ...ai, api_key: e.target.value })}
                placeholder={aiKeySet ? '•••••••• (configurada)' : 'Cole sua chave do Google AI Studio'}
                className="bg-white border-gray-300 text-gray-900 font-mono"
                data-testid="ai-key-input"
              />
              <p className="text-xs text-gray-500">
                Obtenha em <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-[#003366] underline">aistudio.google.com/app/apikey</a>
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Modelo</Label>
              <Input
                value={ai.model}
                onChange={(e) => setAi({ ...ai, model: e.target.value })}
                placeholder="gemini-3.8-flash"
                className="bg-white border-gray-300 text-gray-900 font-mono"
                data-testid="ai-model-input"
              />
              <p className="text-xs text-gray-500">Ex.: gemini-3.8-flash, gemini-2.5-flash, gemini-2.5-pro</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="space-y-0.5">
              <Label className="text-gray-700 font-medium">Validação por IA ativa</Label>
              <p className="text-xs text-gray-500">Habilita o botão "Validar com IA" nas licenças</p>
            </div>
            <Switch
              checked={ai.enabled}
              onCheckedChange={(checked) => setAi({ ...ai, enabled: checked })}
              className="data-[state=checked]:bg-[#003366]"
              data-testid="ai-enabled-toggle"
            />
          </div>

          {aiTestResult && (
            <div className={`flex items-center gap-2 text-sm p-3 rounded-lg border ${aiTestResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`} data-testid="ai-test-result">
              {aiTestResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {aiTestResult.message}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <Button onClick={handleSaveAi} disabled={savingAi} className="bg-[#003366] hover:bg-[#002244]" data-testid="save-ai-btn">
              <Save className="w-4 h-4 mr-2" />
              {savingAi ? 'Salvando...' : 'Salvar Configuração de IA'}
            </Button>
            <Button onClick={handleTestAi} disabled={testingAi} variant="outline" className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="test-ai-btn">
              <TestTube className={`w-4 h-4 mr-2 ${testingAi ? 'animate-pulse' : ''}`} />
              {testingAi ? 'Testando...' : 'Testar conexão'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <Bell className="w-5 h-5 text-[#003366]" />
            Notificações de Vencimento
          </CardTitle>
          <CardDescription>Configure quando e para quem enviar alertas de licenças</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-gray-700 font-medium">Ativar Notificações</Label>
              <p className="text-sm text-gray-500">Habilita o envio de emails de alerta de vencimento</p>
            </div>
            <Switch
              checked={settings.notification_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, notification_enabled: checked })}
              className="data-[state=checked]:bg-[#003366]"
              data-testid="notification-toggle"
            />
          </div>

          {/* Days Before */}
          <div className="space-y-2">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Dias de Antecedência para Aviso
            </Label>
            <Input
              value={daysInput}
              onChange={(e) => setDaysInput(e.target.value)}
              placeholder="Ex: 30, 15, 7"
              className="bg-white border-gray-300 text-gray-900"
              data-testid="notification-days-input"
            />
            <p className="text-sm text-gray-500">Informe os dias separados por vírgula</p>
          </div>

          {/* Frequency */}
          <div className="space-y-2">
            <Label className="text-gray-700 font-medium">Frequência de Verificação</Label>
            <Select
              value={settings.notification_frequency}
              onValueChange={(value) => setSettings({ ...settings, notification_frequency: value })}
            >
              <SelectTrigger className="bg-white border-gray-300 text-gray-700" data-testid="frequency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="daily">Diariamente</SelectItem>
                <SelectItem value="weekly">Semanalmente</SelectItem>
                <SelectItem value="on_trigger">Apenas manual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recipients Section */}
          <div className="space-y-4">
            <Label className="text-gray-700 font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Destinatários dos Alertas
            </Label>

            {/* Current Recipients */}
            {settings.recipients.length > 0 && (
              <div className="space-y-2">
                {settings.recipients.map((r) => (
                  <div key={r.email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={r.type === 'user' ? 'bg-blue-50 text-[#003366] border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                        {r.type === 'user' ? 'Usuário' : 'Externo'}
                      </Badge>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{r.name}</p>
                        <p className="text-xs text-gray-500">{r.email}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRecipient(r.email)}
                      className="h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50"
                      data-testid={`remove-recipient-${r.email}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add User Recipient */}
            {availableUsers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-gray-600">Adicionar usuário do sistema</Label>
                <div className="flex gap-2">
                  <Select onValueChange={addUserRecipient}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-700 flex-1" data-testid="add-user-recipient-select">
                      <SelectValue placeholder="Selecione um usuário..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {availableUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Add External Email */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Adicionar email externo</Label>
              <div className="flex gap-2">
                <Input
                  value={externalName}
                  onChange={(e) => setExternalName(e.target.value)}
                  placeholder="Nome"
                  className="bg-white border-gray-300 text-gray-900 w-1/3"
                  data-testid="external-name-input"
                />
                <Input
                  type="email"
                  value={externalEmail}
                  onChange={(e) => setExternalEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  className="bg-white border-gray-300 text-gray-900 flex-1"
                  data-testid="external-email-input"
                />
                <Button
                  onClick={addExternalRecipient}
                  variant="outline"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  data-testid="add-external-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </Button>
            <Button
              onClick={handleTestEmail}
              disabled={testing || settings.recipients.length === 0}
              variant="outline"
              className="bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              data-testid="test-email-btn"
            >
              <TestTube className="w-4 h-4 mr-2" />
              {testing ? 'Enviando...' : 'Testar Email'}
            </Button>
            <Button
              onClick={handleSendNotifications}
              disabled={sending || !settings.notification_enabled || settings.recipients.length === 0}
              variant="outline"
              className="bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="send-notifications-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Enviando...' : 'Enviar Notificações Agora'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
